import { DateTimePicker, TextInput } from "@carbon6/c6-frontend-design";
import type { ReactNode } from "react";
import { describeCronExpression, validateCronExpression } from "@/modules/training/public";
import { HatTextTooltip } from "@/components/tooltip";
import {
  dateTimeValueToDatetimeLocal,
  datetimeLocalToDateTimeValue,
  minFreeGpusGuide,
  scheduleMinFreeGpus,
  type ScheduleDraft,
} from "./scheduleDraft";
import { SubmitField, ToggleField } from "./submitFields";
import { TimeZoneField } from "./TimeZoneField";
import styles from "./scheduleFields.module.css";

/**
 * Schedule 입력 필드 묶음. 생성 스텝(ScheduleStep)과 편집 다이얼로그(ScheduleEditDialog)가 같은
 * 컨트롤을 쓴다. 같은 축을 두 화면이 다른 컨트롤·다른 문구로 그리면 판정이 달라진다. 값 판독은
 * `scheduleDraft` 모듈이 소유하고, 이 파일은 입력만 연결한다.
 */

// 잠금 사유가 있는 토글. 켤 수 없는 이유를 툴팁으로 표시한다. 잠긴 토글만 있으면 이유를 알 수 없다.
export function GatedToggle({
  label,
  hint,
  hintPlacement,
  available,
  lockedReason,
  checked,
  onChange,
}: {
  label: string;
  hint?: string;
  hintPlacement?: "inline" | "below";
  available: boolean;
  lockedReason: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}): ReactNode {
  const toggle = (
    <ToggleField
      label={label}
      hint={available ? hint : undefined}
      hintPlacement={hintPlacement}
      checked={available ? checked : false}
      disabled={!available}
      onChange={onChange}
    />
  );
  if (available) {
    return toggle;
  }
  return (
    <HatTextTooltip text={lockedReason}>
      <div>{toggle}</div>
    </HatTextTooltip>
  );
}

// cron 표현식과 시간대. 식은 좁은 칸에, 그 뜻은 오른쪽에 굵게 표시한다(사용자가 읽는 것은 풀이다).
export function CronRecurrenceFields({
  cron,
  timezone,
  required = false,
  // 식을 최종 확인하는 시점을 나타내는 동사. 생성은 "제출할 때", 편집은 "저장할 때".
  confirmVerb,
  onChange,
}: {
  cron: string;
  timezone: string;
  required?: boolean;
  confirmVerb: string;
  onChange: (patch: Partial<Pick<ScheduleDraft, "cron" | "timezone">>) => void;
}): ReactNode {
  const validation = validateCronExpression(cron);
  const description = describeCronExpression(cron);
  return (
    <>
      <SubmitField
        label="cron 표현식"
        required={required}
        hint={
          validation.kind === "invalid"
            ? validation.message
            : `분·시·일·월·요일 다섯 자리를 띄어 적습니다. 예: 0 2 * * * 는 매일 02:00, 0 */6 * * * 는 6시간마다, 0 9 * * 1 은 매주 월요일 09:00 입니다. 식이 맞는지는 ${confirmVerb} 서버가 다시 확인합니다.`
        }
      >
        <div className={styles.cronRow}>
          <TextInput
            value={cron}
            placeholder="0 2 * * *"
            aria-label="cron 표현식"
            invalid={validation.kind === "invalid"}
            onChange={(event) => onChange({ cron: event.target.value })}
          />
          {description === null ? null : (
            <span className={styles.cronDescription} title={description}>
              {description}
            </span>
          )}
        </div>
      </SubmitField>
      <SubmitField label="시간대" hint="cron을 읽을 시간대입니다. 비우면 Asia/Seoul로 봅니다.">
        <TimeZoneField value={timezone} onChange={(next) => onChange({ timezone: next })} />
      </SubmitField>
    </>
  );
}

// 여유 GPU 조건. 상류는 free_gpus < 조건이면 보류한다. "N개 이상일 때만 실행"이라는 규칙 문장 안에
// 값을 적는다. 비움의 뜻은 행 상태에 따라 다르다(조건 삭제 vs 유지). 안내문이 그 차이를 표시한다.
export function MinFreeGpusField({
  draft,
  totalGpus,
  clearsExisting,
  placeholder,
  onChange,
}: {
  draft: ScheduleDraft;
  totalGpus: number | null;
  clearsExisting: boolean;
  placeholder?: string;
  onChange: (minFreeGpus: string) => void;
}): ReactNode {
  const value = scheduleMinFreeGpus(draft, totalGpus);
  return (
    <SubmitField label="여유 GPU 조건" hint={minFreeGpusGuide({ totalGpus, clearsExisting })}>
      <div className={styles.minFreeGpusRow}>
        <span className={styles.minFreeGpusText}>여유 GPU가</span>
        <TextInput
          value={draft.minFreeGpus}
          inputMode="numeric"
          placeholder={placeholder}
          aria-label="여유 GPU 조건"
          invalid={value.kind === "invalid"}
          onChange={(event) => onChange(event.target.value)}
        />
        <span className={styles.minFreeGpusText}>개 이상일 때만 실행</span>
      </div>
    </SubmitField>
  );
}

// 시작·종료 시각. c6 DateTimePicker 를 datetime-local 문자열 경계로 감싼다. 초안은 문자열을 정본으로
// 가진다(판독기·PATCH 빌더·요약이 문자열을 읽는다). 변환은 피커 경계에서만 한다.
export function ScheduleDateTimePicker({
  value,
  ariaLabel,
  onChange,
}: {
  value: string;
  ariaLabel: string;
  onChange: (local: string) => void;
}): ReactNode {
  return (
    <DateTimePicker
      value={datetimeLocalToDateTimeValue(value)}
      onChange={(next) => onChange(dateTimeValueToDatetimeLocal(next))}
      // 달력·트리거는 한국어 로캘을 쓴다. 시각 칸 머리글(Hour/Minute)과 Now 는 c6 가 영어로 그린다.
      // 한글 두 글자는 버튼 폭에서 세로로 깨지므로 그 부분은 기본값으로 둔다.
      locale="ko-KR"
      placeholder="날짜와 시각을 고르세요"
      aria-label={ariaLabel}
      confirmLabel="확인"
      cancelLabel="취소"
      adjustNotice={(adjusted) =>
        `가장 가까운 유효 시각(${String(adjusted.hours).padStart(2, "0")}:${String(
          adjusted.minutes,
        ).padStart(2, "0")})으로 맞췄습니다.`
      }
    />
  );
}
